import { useNavigate } from 'react-router-dom';
import { usePromoPlans, type PromoPlan } from '@features/subscriptions/hooks/usePromoPlans';

/**
 * Banner de campaña promocional.
 *
 * Se muestra SOLO si el catálogo tiene algún plan con `limits.promo === true`, y
 * los precios, nombres y ahorros salen de esos planes. Encender o apagar la
 * campaña es marcar el flag en el panel de suscripciones: no hay que desplegar
 * el portal ni editar textos, y nunca anuncia un precio que no exista.
 *
 * Usa los colores de la pieza de diseño (#7B2382 morado de campaña, #14243A
 * navy) en vez de los tokens del portal: la campaña tiene identidad propia y su
 * morado es más brillante que el `primary` de la marca.
 */

/** El plan más caro de la promo se presenta como el favorito (efecto ancla). */
function TarjetaPromo({
  plan,
  destacada,
  onComprar,
}: {
  plan: PromoPlan;
  destacada: boolean;
  onComprar: () => void;
}) {
  const ahorro =
    plan.precioRegular != null && plan.precioRegular > plan.precio
      ? Math.round(((plan.precioRegular - plan.precio) / plan.precioRegular) * 100)
      : null;

  if (destacada) {
    return (
      <div className="flex flex-col gap-3 rounded-[14px] border border-[#14243A] bg-[#14243A] p-5 shadow-[0_16px_32px_-22px_rgba(20,36,58,0.7)]">
        <div className="flex min-h-[22px] flex-wrap items-center gap-2">
          <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#E3C9E8]">
            {plan.nombre}
          </span>
          <span className="rounded-full bg-[#7B2382] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.1em] text-white">
            ★ El favorito
          </span>
        </div>

        <div className="flex flex-nowrap items-baseline gap-1.5 whitespace-nowrap">
          <span className="text-[32px] font-extrabold tracking-[-0.02em] text-white">
            ${plan.precio.toFixed(2)}
          </span>
          <span className="text-[13px] font-semibold text-[#B9C1CE]">/primer año</span>
        </div>

        {plan.precioRegular != null && (
          <span className="text-[13px] text-[#8A93A3] line-through">
            ${plan.precioRegular.toFixed(2)}/año
          </span>
        )}

        {plan.destacado && (
          <p className="text-[12.5px] font-semibold leading-[1.4] text-white">{plan.destacado}</p>
        )}

        {plan.precioRegular != null && (
          <p className="text-[11.5px] leading-[1.45] text-[#B9C1CE]">
            {ahorro != null && `Ahorra ${ahorro}% — `}
            A partir del segundo año ${plan.precioRegular.toFixed(2)}/año
          </p>
        )}

        <button
          type="button"
          onClick={onComprar}
          className="mt-auto min-h-[44px] rounded-[10px] bg-[#7B2382] px-3.5 py-3 text-sm font-bold text-white transition-colors hover:bg-[#932C9B] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#7B2382]"
        >
          Voy con todo
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 rounded-[14px] border border-[#E2DAE4] bg-[#FBF9FB] p-5 dark:border-secondary-700 dark:bg-secondary-900">
      <div className="flex min-h-[22px] items-center">
        <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#7B2382] dark:text-[#C9A6CE]">
          {plan.nombre}
        </span>
      </div>

      <div className="flex flex-nowrap items-baseline gap-1.5 whitespace-nowrap">
        <span className="text-[32px] font-extrabold tracking-[-0.02em] text-[#14243A] dark:text-white">
          ${plan.precio.toFixed(2)}
        </span>
        <span className="text-[13px] font-semibold text-[#5C6473] dark:text-secondary-400">
          /primer año
        </span>
      </div>

      {plan.precioRegular != null && (
        <span className="text-[13px] text-[#9AA0AC] line-through">
          ${plan.precioRegular.toFixed(2)}/año
        </span>
      )}

      {plan.destacado && (
        <p className="text-[12.5px] font-semibold leading-[1.4] text-[#14243A] dark:text-white">
          {plan.destacado}
        </p>
      )}

      {plan.precioRegular != null && (
        <p className="text-[11.5px] leading-[1.45] text-[#6E7683] dark:text-secondary-400">
          {ahorro != null && `Ahorra ${ahorro}% — `}
          A partir del segundo año ${plan.precioRegular.toFixed(2)}/año
        </p>
      )}

      <button
        type="button"
        onClick={onComprar}
        className="mt-auto min-h-[44px] rounded-[10px] border-[1.5px] border-[#C9A6CE] bg-white px-3.5 py-3 text-sm font-bold text-[#7B2382] transition-colors hover:border-[#7B2382] hover:bg-[#F3E9F4] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#7B2382] dark:bg-secondary-800 dark:text-[#C9A6CE] dark:hover:bg-secondary-700"
      >
        Hazlo crecer hoy
      </button>
    </div>
  );
}

export function PromoDecimoBanner() {
  const navigate = useNavigate();
  const { promos, hayPromo, cierreCampana } = usePromoPlans();

  // Sin campaña activa en el catálogo, el banner no existe.
  if (!hayPromo) return null;

  /**
   * Lleva al plan concreto y abre su pago, en vez de dejar al usuario en el
   * listado buscando cuál era. El banner ya le vendió un plan y un precio: que
   * tenga que reencontrarlo es donde se cae la conversión.
   *
   * Si ese plan no se puede comprar —ya lo tiene, o es una bajada de nivel—
   * PlansPage aterriza en el listado sin abrir nada.
   */
  const comprarPlan = (planId: string) =>
    navigate('/subscriptions/plans', { state: { comprarPlanId: planId } });

  return (
    <section className="relative overflow-hidden rounded-[20px] border border-[#E2DAE4] bg-white shadow-[0_18px_44px_-28px_rgba(20,36,58,0.35)] dark:border-secondary-700 dark:bg-secondary-800">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-20 -top-32 h-[380px] w-[380px] rounded-full"
        style={{
          background:
            'radial-gradient(circle, rgba(123,35,130,0.10), rgba(123,35,130,0) 70%)',
        }}
      />

      <div className="relative grid gap-8 p-7 sm:p-9 xl:grid-cols-[1.05fr_1.35fr] xl:items-center xl:gap-10">
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex w-fit items-center rounded-full bg-[#F3E9F4] px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-[#7B2382] dark:bg-[#7B2382]/20 dark:text-[#E3C9E8]">
              Promo Décimo
            </span>

            {/* El cierre va junto a la etiqueta de campaña, donde se lee antes
                que el precio: es lo que le pone reloj a la oferta. En la recta
                final se pinta en ámbar para que salte por encima del morado. */}
            {cierreCampana && (
              <span
                className={
                  cierreCampana.urgente
                    ? 'inline-flex w-fit items-center gap-1.5 rounded-full bg-[#B45309] px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-white'
                    : 'inline-flex w-fit items-center gap-1.5 rounded-full border border-[#E2DAE4] px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-[#5C6473] dark:border-secondary-600 dark:text-secondary-300'
                }
              >
                {cierreCampana.urgente && <span aria-hidden="true">⏳</span>}
                {cierreCampana.etiqueta}
              </span>
            )}
          </div>

          <h2 className="text-[28px] font-extrabold leading-[1.1] tracking-[-0.02em] text-[#14243A] text-pretty sm:text-[34px] dark:text-white">
            El plan Free te queda bien… hasta que tu negocio empieza a crecer. 😉
          </h2>

          <p className="max-w-[52ch] text-[15px] leading-[1.55] text-[#5C6473] text-pretty dark:text-secondary-400">
            Haz más con Factura Libre: factura desde tu celular, personaliza tu factura
            con tu logo y prepárate para el siguiente nivel de tu negocio.
          </p>
        </div>

        <div
          className={
            promos.length > 1 ? 'grid gap-4 sm:grid-cols-2' : 'grid gap-4 sm:max-w-sm'
          }
        >
          {promos.map((plan, i) => (
            <TarjetaPromo
              key={plan.id}
              plan={plan}
              // Con dos o más, el más caro ancla; con una sola, va destacada.
              destacada={promos.length === 1 || i === promos.length - 1}
              onComprar={() => comprarPlan(plan.id)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
